/**
 * A missed call or an inbound text from a number on no lead, opened from
 * Messages. Three ways out:
 *   LINK TO A LEAD   the files that have the number (if any appeared since), or
 *                    a lead search. Choosing one puts it there (its feed entry)
 *                    and opens the file.
 *   CREATE NEW FILE  a lead with this phone (and the name, if typed), New Lead,
 *                    source Direct call / Direct text, assigned to you; every
 *                    unmatched item from the number moves onto it; it opens.
 *   SPAM             read and dismissed, no file. "Ignore this number from now
 *                    on" keeps its future calls and texts out of Messages and
 *                    the feed (admins can undo it in Settings).
 * Opening it does not mark it read; Create new file and Spam do.
 */

import React, { useEffect, useState } from 'react';
import { useApiClient } from '../hooks/useApi';
import type { RingCentralLeadLabel } from '../services/api';
import { formatJobNo } from '../lib/jobNumber';
import { formatDateTime } from '../lib/utils';

const leadText = (l: RingCentralLeadLabel) =>
  `${`${l.first_name ?? ''} ${l.last_name ?? ''}`.trim() || 'Unnamed'} · ${formatJobNo(l)} · ${l.stage ?? '—'}`;

const field: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: '13px', borderRadius: '6px',
  background: 'var(--crm-field)', color: 'var(--crm-text)', border: '1px solid var(--crm-field-line)',
};
const btn: React.CSSProperties = {
  padding: '7px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
  background: 'var(--crm-panel-2)', color: 'var(--crm-text)', border: '1px solid var(--crm-line)',
};
const rowBtn: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', padding: '7px 9px', fontSize: '12px',
  background: 'transparent', color: 'var(--crm-text)', border: 'none', borderBottom: '1px solid var(--crm-line)', cursor: 'pointer',
};

interface Info { number: string; at: string | null; preview: string | null; candidates: RingCentralLeadLabel[] }

const phoneText = (d: string | null | undefined, fallback: string | null | undefined) =>
  d && d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (fallback || 'Unknown number');

export function MissedCallLookup({ callId, kind = 'call', onClose, onOpenLead }: {
  /** The call's id — or, with kind 'text', the text's (ringcentral_messages.id). */
  callId: string; kind?: 'call' | 'text'; onClose: () => void; onOpenLead: (leadId: string) => void;
}) {
  const api = useApiClient();
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<RingCentralLeadLabel[]>([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [ignoreNumber, setIgnoreNumber] = useState(false);

  const createFile = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.createLeadFromRingCentral(kind, callId, name);
      window.dispatchEvent(new Event('crm:badges-changed'));
      if (res.data?.lead_id) onOpenLead(res.data.lead_id);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not create the file.');
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.dismissRingCentralItem(kind, callId, ignoreNumber);
      window.dispatchEvent(new Event('crm:badges-changed'));
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not dismiss it.');
      setBusy(false);
    }
  };

  useEffect(() => {
    const load = kind === 'text'
      ? api.getRingCentralText(callId).then((r) => {
          const t = r.data?.text;
          return t ? { digits: t.counterparty_digits, info: { number: phoneText(t.counterparty_digits, t.from_number), at: t.rc_created_at, preview: t.body ?? null, candidates: r.data?.candidates ?? [] } } : null;
        })
      : api.getRingCentralCall(callId).then((r) => {
          const c = r.data?.call;
          return c ? { digits: c.counterparty_digits, info: { number: phoneText(c.counterparty_digits, c.from_number), at: c.started_at, preview: null, candidates: r.data?.candidates ?? [] } } : null;
        });
    load
      .then((x) => { setInfo(x?.info ?? null); if (x?.digits) setQ(x.digits); })
      .catch((e) => setError(e?.message || `Could not load the ${kind}.`));
  }, [api, callId, kind]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setFound([]); return; }
    const t = setTimeout(() => {
      api.searchLeads({ q: term, page_size: 8 })
        .then((r) => setFound((r.data?.rows || []).map((l: any) => ({ id: l.id, first_name: l.first_name, last_name: l.last_name, job_no: l.job_no, stage: l.stage }))))
        .catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [api, q]);

  const choose = async (leadId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'text') await api.linkRingCentralText(callId, leadId);
      else await api.saveRingCentralCallNotes(callId, { lead_id: leadId });
      window.dispatchEvent(new CustomEvent('ringcentral:call-logged', { detail: { leadId } }));
      onOpenLead(leadId);
      onClose();
    } catch (e: any) {
      setError(e?.message || `Could not put the ${kind} on that file.`);
      setBusy(false);
    }
  };

  const candidates = info?.candidates ?? [];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
      <div role="dialog" aria-modal="true" aria-label={kind === 'text' ? 'Text' : 'Missed call'} onClick={(e) => e.stopPropagation()}
           style={{ background: 'var(--crm-panel)', color: 'var(--crm-text)', border: '1px solid var(--crm-line)', borderRadius: '12px', padding: '18px', width: 'min(440px, 100%)', boxShadow: '0 20px 60px var(--crm-shadow-color)' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>{kind === 'text' ? 'Text from a number on no lead' : 'Missed call'}</div>
        <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: "'DM Mono', monospace", marginBottom: '2px' }}>{info?.number ?? ''}</div>
        <div style={{ fontSize: '12px', color: 'var(--crm-muted)', marginBottom: info?.preview ? '6px' : '14px' }}>
          {info?.at ? formatDateTime(info.at) : ''}
        </div>
        {info?.preview && (
          <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', background: 'var(--crm-comm-text-tint)', border: '1px solid var(--crm-comm-text-line)', borderRadius: '8px', padding: '8px 10px', marginBottom: '14px' }}>{info.preview}</div>
        )}

        {!info && !error && <div style={{ fontSize: '12px', color: 'var(--crm-muted)' }}>Loading…</div>}
        {info && <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Link to a lead</div>}

        {candidates.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '4px' }}>Files with this number</div>
            <div style={{ border: '1px solid var(--crm-line)', borderRadius: '6px' }}>
              {candidates.map((l) => <button key={l.id} type="button" disabled={busy} onClick={() => choose(l.id)} style={rowBtn}>{leadText(l)}</button>)}
            </div>
          </div>
        )}

        {info && (
          <>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '4px' }}>
              {candidates.length ? 'Or find another file' : 'No lead has this number. Find the file:'}
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, job # or phone…" style={field} autoFocus />
            {found.length > 0 && (
              <div style={{ border: '1px solid var(--crm-line)', borderRadius: '6px', marginTop: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                {found.map((l) => <button key={l.id} type="button" disabled={busy} onClick={() => choose(l.id)} style={rowBtn}>{leadText(l)}</button>)}
              </div>
            )}
            <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '6px' }}>Choosing a file puts this {kind === 'text' ? 'text' : 'missed call'} on it and opens it.</div>
          </>
        )}

        {info && (
          <div style={{ display: 'grid', gap: '10px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--crm-line)' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '4px' }}>Or create a new file for this number</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" style={{ ...field, flex: '1 1 180px', width: 'auto' }} />
                <button type="button" disabled={busy} onClick={createFile}
                        style={{ ...btn, background: 'var(--crm-accent)', color: 'var(--crm-on-fill)', border: '1px solid var(--crm-accent)', fontWeight: 600 }}>
                  Create new file
                </button>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '4px' }}>New Lead · source {kind === 'text' ? 'Direct text' : 'Direct call'} · assigned to you</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" disabled={busy} onClick={dismiss} style={{ ...btn, color: 'var(--crm-red)', borderColor: 'var(--crm-red-soft-line)' }}>
                Spam / not relevant
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={ignoreNumber} onChange={(e) => setIgnoreNumber(e.target.checked)} />
                Ignore this number from now on
              </label>
            </div>
          </div>
        )}

        {error && <div role="alert" style={{ fontSize: '12px', color: 'var(--crm-red)', marginTop: '10px' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button type="button" onClick={onClose} style={btn}>Close</button>
        </div>
      </div>
    </div>
  );
}
